import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { baseUrl, assetPrimaryLabel, assetFullLabel } from '../../shared';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const STATUS_OPTIONS = ['planned', 'in_progress', 'completed', 'cancelled'];
const STATUS_LABELS = { planned: 'Planned', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

export default function VisitModal({ open, onClose, visit, companies, onSaved }) {
  const isAdmin = localStorage.getItem('canManageSchedule') === '1';
  const isStaffOnly = !isAdmin && localStorage.getItem('canViewSchedule') === '1';

  const emptyForm = {
    company: '', site_locations: [], start_date: '', end_date: '',
    work_type_id: '', sow: '', po_number: '', work_order_number: '',
    job_card_number: '', quoted_labour_hours: '', comments: '',
    status: 'planned', staffIds: [], catNos: [], logistics: [],
  };

  const [form, setForm] = useState(emptyForm);
  const [allSites, setAllSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [logisticsTypes, setLogisticsTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load reference data and reset form on open/visit change
  useEffect(() => {
    if (!open) return;
    Promise.all([
      fetch(baseUrl + 'schedule/work-types', { headers: authHeaders() }).then(r => r.json()),
      fetch(baseUrl + 'schedule/logistics-types', { headers: authHeaders() }).then(r => r.json()),
    ]).then(([wt, lt]) => {
      setWorkTypes(Array.isArray(wt) ? wt : []);
      setLogisticsTypes(Array.isArray(lt) ? lt : []);
    });

    if (visit) {
      setForm({
        company: visit.company || '',
        site_locations: visit.sites || [],
        // Use local-time formatting (matches how the calendar/detail/list views render the dates).
        // The API returns dates as UTC-midnight ISO strings; .split('T')[0] took the UTC date part,
        // which displayed a day early in +UTC timezones — dayjs().format converts back to local first.
        start_date: visit.start_date ? dayjs(visit.start_date).format('YYYY-MM-DD') : '',
        end_date: visit.end_date ? dayjs(visit.end_date).format('YYYY-MM-DD') : '',
        work_type_id: visit.work_type_id || '',
        sow: visit.sow || '',
        po_number: visit.po_number || '',
        work_order_number: visit.work_order_number || '',
        job_card_number: visit.job_card_number || '',
        quoted_labour_hours: visit.quoted_labour_hours || '',
        comments: visit.comments || '',
        status: visit.status || 'planned',
        staffIds: (visit.staff || []).map(s => s.user_id),
        catNos: (visit.assets || []).map(a => a.cat_no),
        logistics: (visit.logistics || []).map(l => ({ logistics_type_id: l.logistics_type_id, details: l.details || '' })),
      });
    } else {
      setForm(emptyForm);
      setAllSites([]);
      setAssets([]);
    }
  }, [open, visit]);

  // Re-fetch staff with conflict info whenever dates change
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams();
    if (form.start_date && form.end_date) {
      params.set('start_date', form.start_date);
      params.set('end_date', form.end_date);
      if (visit?.id) params.set('exclude_visit_id', visit.id);
    }
    const qs = params.toString();
    fetch(baseUrl + 'schedule/staff' + (qs ? '?' + qs : ''), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setStaffList(Array.isArray(d) ? d : []));
  }, [open, form.start_date, form.end_date, visit?.id]);

  // Load sites when company changes
  useEffect(() => {
    if (!form.company) { setAllSites([]); setAssets([]); return; }
    fetch(baseUrl + 'schedule/sites?company=' + encodeURIComponent(form.company), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAllSites(Array.isArray(d) ? d : []));
    if (!visit || visit.company !== form.company) {
      setForm(f => ({ ...f, site_locations: [], catNos: [] }));
      setAssets([]);
    }
  }, [form.company]);

  // Load assets when selected sites change
  useEffect(() => {
    if (!form.company || form.site_locations.length === 0) { setAssets([]); return; }
    const sitesParam = form.site_locations.map(encodeURIComponent).join(',');
    fetch(baseUrl + 'schedule/assets?company=' + encodeURIComponent(form.company) + '&sites=' + sitesParam, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAssets(Array.isArray(d) ? d : []));
  }, [form.company, form.site_locations]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // AssetId/CatNo display follows the shared rule (assetPrimaryLabel / assetFullLabel in shared.js).
  // assetDetails is local — item/manufacturer/model is specific to this picker.
  const assetDetails = (a) => [a.item, a.manufacturer, a.model].filter(Boolean).join(' ');

  const toggleLogisticsType = (ltId) => {
    setForm(f => {
      const exists = f.logistics.find(l => l.logistics_type_id === ltId);
      if (exists) return { ...f, logistics: f.logistics.filter(l => l.logistics_type_id !== ltId) };
      return { ...f, logistics: [...f.logistics, { logistics_type_id: ltId, details: '' }] };
    });
  };

  const setLogisticsDetails = (ltId, details) => {
    setForm(f => ({
      ...f,
      logistics: f.logistics.map(l => l.logistics_type_id === ltId ? { ...l, details } : l),
    }));
  };

  const handleSave = async () => {
    if (!form.company || form.site_locations.length === 0 || !form.start_date || !form.end_date) {
      setError('Company, at least one site, start date and end date are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const method = visit ? 'PATCH' : 'POST';
      const url = baseUrl + 'schedule/visits' + (visit ? '/' + visit.id : '');
      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          work_type_id: form.work_type_id || null,
          quoted_labour_hours: form.quoted_labour_hours || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Save failed'); setSaving(false); return; }
      onSaved();
      onClose();
    } catch (e) {
      setError('Network error');
    }
    setSaving(false);
  };

  const handleStatusUpdate = async () => {
    setSaving(true);
    setError('');
    const res = await fetch(baseUrl + 'schedule/visits/' + visit.id, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ status: form.status }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.message || 'Update failed'); } else { onSaved(); onClose(); }
    setSaving(false);
  };

  // Pre-compute asset grouping metadata for the two-level site → type dropdown
  const sortedAssets = [...assets].sort((a, b) => {
    const sA = a.siteLocation || '', sB = b.siteLocation || '';
    if (sA !== sB) return sA.localeCompare(sB);
    return (a.type || '').localeCompare(b.type || '');
  });
  const assetSiteCatNos = {};
  const assetGroupMeta = {};
  const seenAssetSites = new Set();
  sortedAssets.forEach(a => {
    const site = a.siteLocation || '', type = a.type || 'No Type';
    const key = `${site}|||${type}`;
    if (!assetSiteCatNos[site]) assetSiteCatNos[site] = [];
    assetSiteCatNos[site].push(a.catNo);
    if (!assetGroupMeta[key]) {
      assetGroupMeta[key] = { isFirstOfSite: !seenAssetSites.has(site), typeCatNos: [] };
      seenAssetSites.add(site);
    }
    assetGroupMeta[key].typeCatNos.push(a.catNo);
  });
  Object.keys(assetGroupMeta).forEach(key => {
    assetGroupMeta[key].siteCatNos = assetSiteCatNos[key.split('|||')[0]] || [];
  });

  // Staff that are currently selected but have conflicts with the chosen dates
  const selectedWithConflicts = staffList.filter(s =>
    form.staffIds.includes(s.id) && s.conflicts && s.conflicts.length > 0
  );

  const formatConflictDates = (conflicts) =>
    conflicts.map(c =>
      `${dayjs(c.start_date).format('DD/MM/YY')}–${dayjs(c.end_date).format('DD/MM/YY')} (${c.company})`
    ).join(', ');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { mb: '6vh', maxHeight: 'calc(100vh - 8vh)' } }}>
      <DialogTitle>{visit ? (isAdmin ? 'Edit Visit' : 'Update Status') : 'Create Site Visit'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isStaffOnly && visit ? (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select value={form.status} label="Status" onChange={e => set('status', e.target.value)}>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>
        ) : (
          <Grid container spacing={2}>
            {/* Company */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Company</InputLabel>
                <Select value={form.company} label="Company" onChange={e => set('company', e.target.value)}>
                  {(companies || []).map(c => <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Sites — multi-select */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                disabled={!form.company}
                options={allSites}
                value={form.site_locations}
                onChange={(_, val) => { set('site_locations', val); set('catNos', []); }}
                renderInput={params => (
                  <TextField {...params} required label={form.company ? 'Sites (select all that apply)' : 'Select a company first'} />
                )}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => <Chip label={opt} size="small" {...getTagProps({ index: idx })} />)
                }
              />
            </Grid>

            {/* Dates */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                value={form.start_date} onChange={e => set('start_date', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label="End Date" InputLabelProps={{ shrink: true }}
                value={form.end_date} onChange={e => set('end_date', e.target.value)} />
            </Grid>

            {/* Work type + Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Work Type</InputLabel>
                <Select value={form.work_type_id} label="Work Type" onChange={e => set('work_type_id', e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {workTypes.map(wt => <MenuItem key={wt.id} value={wt.id}>{wt.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={e => set('status', e.target.value)}>
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* SOW */}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Scope of Work (SOW)"
                value={form.sow} onChange={e => set('sow', e.target.value)} />
            </Grid>

            {/* Reference numbers */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="PO Number" value={form.po_number} onChange={e => set('po_number', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Work Order Number" value={form.work_order_number} onChange={e => set('work_order_number', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Job Card Number" value={form.job_card_number} onChange={e => set('job_card_number', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Quoted Labour Hours"
                value={form.quoted_labour_hours} onChange={e => set('quoted_labour_hours', e.target.value)} />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">STAFF</Typography></Divider></Grid>

            {/* Conflict warning for already-selected staff */}
            {selectedWithConflicts.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {visit ? 'These dates conflict with other bookings for selected staff:' : 'Selected staff have conflicting bookings for these dates:'}
                  </Typography>
                  {selectedWithConflicts.map(s => (
                    <Typography key={s.id} variant="body2">
                      <strong>{s.name}</strong> — already booked {formatConflictDates(s.conflicts)}
                    </Typography>
                  ))}
                </Alert>
              </Grid>
            )}

            {/* Staff */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={staffList}
                getOptionLabel={o => o.name + (o.company ? ` (${o.company})` : '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={staffList.filter(s => form.staffIds.includes(s.id))}
                onChange={(_, val) => set('staffIds', val.map(v => v.id))}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ py: 0.25 }}>
                      <Typography variant="body2">
                        {option.name}{option.company ? ` (${option.company})` : ''}
                      </Typography>
                      {option.conflicts && option.conflicts.length > 0 && (
                        <Typography variant="caption" color="error.main">
                          Unavailable — booked: {formatConflictDates(option.conflicts)}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={params => (
                  <TextField {...params} label={
                    form.start_date && form.end_date
                      ? 'Assigned Staff (greyed = unavailable for these dates)'
                      : 'Assigned Staff'
                  } />
                )}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => <Chip label={opt.name} {...getTagProps({ index: idx })} />)
                }
              />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">ASSETS</Typography></Divider></Grid>

            {/* Assets */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={form.site_locations.length === 0}
                options={sortedAssets}
                groupBy={o => `${o.siteLocation || ''}|||${o.type || 'No Type'}`}
                getOptionLabel={o => `${assetFullLabel(o)} — ${assetDetails(o)}`}
                isOptionEqualToValue={(opt, val) => opt.catNo === val.catNo}
                value={sortedAssets.filter(a => form.catNos.includes(a.catNo))}
                onChange={(_, val) => set('catNos', val.map(v => v.catNo))}
                renderGroup={(params) => {
                  const sepIdx = params.group.indexOf('|||');
                  const site = sepIdx >= 0 ? params.group.slice(0, sepIdx) : params.group;
                  const type = sepIdx >= 0 ? params.group.slice(sepIdx + 3) : 'No Type';
                  const { isFirstOfSite = false, siteCatNos = [], typeCatNos = [] } = assetGroupMeta[params.group] || {};
                  const allSiteSelected = siteCatNos.length > 0 && siteCatNos.every(cn => form.catNos.includes(cn));
                  const allTypeSelected = typeCatNos.length > 0 && typeCatNos.every(cn => form.catNos.includes(cn));
                  return (
                    <li key={params.key}>
                      {isFirstOfSite && (
                        <Box sx={{
                          px: 2, py: 0.5,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          bgcolor: 'grey.200',
                          position: 'sticky', top: -8, zIndex: 2,
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {site || 'No Location'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                            onMouseDown={e => {
                              e.preventDefault();
                              if (allSiteSelected) {
                                set('catNos', form.catNos.filter(cn => !siteCatNos.includes(cn)));
                              } else {
                                set('catNos', [...new Set([...form.catNos, ...siteCatNos])]);
                              }
                            }}
                          >
                            {allSiteSelected ? 'Clear all' : 'Select all'}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{
                        px: 3, py: 0.25,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: 'grey.100',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {type}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                          onMouseDown={e => {
                            e.preventDefault();
                            if (allTypeSelected) {
                              set('catNos', form.catNos.filter(cn => !typeCatNos.includes(cn)));
                            } else {
                              set('catNos', [...new Set([...form.catNos, ...typeCatNos])]);
                            }
                          }}
                        >
                          {allTypeSelected ? 'Clear' : 'Select'}
                        </Typography>
                      </Box>
                      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                  );
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1, py: 0.25 }} size="small" />
                    <Box>
                      <Typography variant="body2">
                        {assetFullLabel(option)} — {assetDetails(option)}
                      </Typography>
                      {option.nextInspectionDate && (
                        <Typography variant="caption" color="text.secondary">
                          Next due: {option.nextInspectionDate}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={params => (
                  <TextField {...params} label={form.site_locations.length > 0 ? `Select Assets (${form.catNos.length} selected)` : 'Select sites first'} />
                )}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => (
                    <Chip label={assetPrimaryLabel(opt)} title={`${assetFullLabel(opt)}${assetDetails(opt) ? ` — ${assetDetails(opt)}` : ''}`} size="small" {...getTagProps({ index: idx })} />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">LOGISTICS</Typography></Divider></Grid>

            {/* Logistics — checkbox + multiline detail */}
            {logisticsTypes.map(lt => {
              const selected = form.logistics.find(l => l.logistics_type_id === lt.id);
              return (
                <Grid item xs={12} key={lt.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => toggleLogisticsType(lt.id)}
                    >
                      <Checkbox checked={!!selected} sx={{ py: 0.5 }} />
                      <Typography>{lt.name}</Typography>
                    </Box>
                    {selected && (
                      <TextField
                        size="small"
                        multiline
                        minRows={2}
                        label="Details / Notes"
                        value={selected.details}
                        onChange={e => setLogisticsDetails(lt.id, e.target.value)}
                        sx={{ width: '100%' }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}

            <Grid item xs={12}><Divider /></Grid>

            {/* Comments */}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Comments"
                value={form.comments} onChange={e => set('comments', e.target.value)} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
        <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={isStaffOnly && visit ? handleStatusUpdate : handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : (visit ? 'Save Changes' : 'Create Visit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
